import React from "react";
import { IonIcon, IonItem } from "@ionic/react";
import { checkmarkCircle } from "ionicons/icons";
import clsx from "clsx";
import { format } from "date-fns";

import boxIcon from "../../assets/icons/box_gradient.svg";
import BoxTitle from "../../atoms/BoxTitle";
import { IBoxDashboard } from "../../state";

import styles from "./BoxAnalysisItem.module.scss";

export interface BoxAnalysisItemProps {
  className?: string;
  isMyBox?: boolean;
  isSingle?: boolean;
  boxData: IBoxDashboard;
  largestRealizationsCount: number;
  onClick?: () => void;
}

const ROOT_TEXT_WIDTH = 37;
const WILL_TEXT_WIDTH = 27;
const EXP_TEXT_WIDTH = 24;

const calculatePercent = (itemValue: number, totalValue: number) =>
  totalValue ? ((itemValue / totalValue) * 100).toFixed(2) : 0;

const BoxAnalysisItem: React.FC<BoxAnalysisItemProps> = ({
  className = "",
  isMyBox = false,
  isSingle = false,
  boxData: {
    title,
    students_count,
    klass_name,
    teacher,
    realizations_count = 0,
    roots_count = 0,
    will_count = 0,
    exp_count = 0,
    favorites_count = 0,
    comments_count = 0,
    avg_realization = 0,
    checked_count,
    latest_realization_created_at,
    is_archived,
    name_status,
    has_unread_realization = false,
  },
  largestRealizationsCount,
  onClick,
}) => {
  const willPercent = React.useMemo(
    () => calculatePercent(roots_count, largestRealizationsCount),
    [roots_count, largestRealizationsCount],
  );

  const rootPercent = React.useMemo(
    () => calculatePercent(will_count, largestRealizationsCount),
    [will_count, largestRealizationsCount],
  );

  const expPercent = React.useMemo(
    () => calculatePercent(exp_count, largestRealizationsCount),
    [exp_count, largestRealizationsCount],
  );

  return (
    <IonItem
      button={!!onClick}
      detail={false}
      className={clsx(styles.boxItem, className)}
      lines="full"
      onClick={onClick}
    >
      <div className={styles.wrapper}>
        <div className={styles.boxInfo}>
          {!isSingle && (
            <>
              <IonIcon className={styles.icon} src={boxIcon} />
              <div className={styles.title}>
                {isMyBox ? (
                  <p>
                    <BoxTitle title="提出されたストック" />
                    {has_unread_realization && (
                      <span className={styles.badge} />
                    )}
                  </p>
                ) : (
                  <>
                    <p>
                      <BoxTitle
                        isArchived={is_archived}
                        nameStatus={name_status}
                        title={title ?? "ー"}
                      />
                      {has_unread_realization && (
                        <span className={styles.badge} />
                      )}
                    </p>
                    <p>{`[${students_count ?? "ー"}名]`}</p>
                  </>
                )}
              </div>
            </>
          )}
          <div className={styles.classInfo}>
            {!isMyBox && <p className={styles.KlassName}>{klass_name}</p>}
            <p className={styles.teacherName}>{teacher.full_name}</p>
          </div>
        </div>
        <div
          className={clsx(
            styles.allRealizationsCount,
            isMyBox && styles.forMyBox,
          )}
        >
          <p>総ストック</p>
          {realizations_count}
          {checked_count !== null && (
            <div className={styles.checkedCount}>
              <IonIcon icon={checkmarkCircle} className={styles.checkIcon} />
              {checked_count}
            </div>
          )}
        </div>
        <div className={styles.realizationAnalysis}>
          <div className={clsx(styles.analysisItem, styles.root)}>
            <div
              style={{
                width: `${willPercent}%`,
              }}
            />
            <span
              style={{ left: `max(${willPercent}%, ${ROOT_TEXT_WIDTH}px)` }}
            >
              {roots_count}
            </span>
          </div>
          <div className={clsx(styles.analysisItem, styles.will)}>
            <div style={{ width: `${rootPercent}%` }} />
            <span
              style={{ left: `max(${rootPercent}%, ${WILL_TEXT_WIDTH}px)` }}
            >
              {will_count}
            </span>
          </div>
          <div className={clsx(styles.analysisItem, styles.exp)}>
            <div style={{ width: `${expPercent}%` }} />
            <span style={{ left: `max(${expPercent}%, ${EXP_TEXT_WIDTH}px)` }}>
              {exp_count}
            </span>
          </div>
        </div>
        <div className={styles.actionCountGroup}>
          {!isMyBox && (
            <>
              <div className={styles.actionCount}>
                いいね
                <div>{favorites_count}</div>
              </div>
              <div className={styles.actionCount}>
                コメント
                <div>{comments_count}</div>
              </div>
              <div className={styles.actionCount}>
                平均ストック
                <div>{avg_realization}</div>
              </div>
            </>
          )}
          {!isSingle && latest_realization_created_at && (
            <span>
              {"最新投稿日 " +
                format(latest_realization_created_at, "yyyy.MM.dd")}
            </span>
          )}
        </div>
      </div>
    </IonItem>
  );
};

export default BoxAnalysisItem;
